import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import { pctToGrams } from '../utils/calculations';
import { SECTIONS, CASING_DB } from '../data/ingredients';
import { normalizeSections } from '../data/methods';
import styles from './PdfExport.module.css';

export default function PdfExport({ calc, onClose }) {
  const [generating, setGenerating] = useState(false);
  const [done,       setDone]       = useState(false);
  const [opts, setOpts] = useState({
    nutrition:    true,
    methods:      true,
    casing:       true,
  });

  const { recipeName, cookMethod, sausageStyle, unitSystem,
    meatWeight, meatWeightG, rating, recipeNotes,
    equipLoss, totals, nutrition, filledIngredients,
    selectedCasing, linkLength, linkWeight, casingResult } = calc;

  const toggleOpt = key => setOpts(o => ({ ...o, [key]: !o[key] }));

  const handleGenerate = () => {
    setGenerating(true);
    setTimeout(() => {
      try   { generatePDF(); setDone(true); }
      catch (e) { alert('PDF error: ' + e.message); }
      finally   { setGenerating(false); }
    }, 100);
  };

  const generatePDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
    const PW  = 215.9, PH = 279.4, ML = 15, MR = 15;
    const CW  = PW - ML - MR;
    let   y   = 18;

    // ── Colours ────────────────────────────────────────────
    const BLACK  = [15,  23,  42];
    const DARK   = [51,  65,  85];
    const MUTED  = [148, 163, 184];
    const ACCENT = [245, 158, 11];
    const GREEN  = [27,  94,  32];
    const BLUE   = [26,  35, 126];
    const RED    = [183, 28,  28];

    const setC = rgb => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
    const setF = rgb => doc.setFillColor(rgb[0], rgb[1], rgb[2]);
    const setD = rgb => doc.setDrawColor(rgb[0], rgb[1], rgb[2]);

    const newPage = () => { doc.addPage(); y = 18; };
    const checkY  = (need = 10) => { if (y + need > PH - 15) newPage(); };

    // ── Helpers ─────────────────────────────────────────────
    const sectionHead = (title, color = DARK) => {
      checkY(12);
      setC(color);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(title, ML, y);
      y += 2;
      setD(color);
      doc.line(ML, y, ML + CW, y);
      y += 5;
    };

    const subHead = (text) => {
      checkY(8);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      setC(BLACK);
      doc.text(text, ML, y);
      y += 5;
    };

    const bodyLine = (text, indent = 0) => {
      checkY(6);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      setC(DARK);
      const lines = doc.splitTextToSize(text, CW - indent);
      lines.forEach(l => {
        checkY(5.5);
        doc.text(l, ML + indent, y);
        y += 5;
      });
    };

    const gap = (h = 4) => { checkY(h); y += h; };

    // ── HEADER (no background — Fix 8) ──────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    setC(BLACK);
    doc.text(recipeName || 'Untitled Recipe', ML, y);
    y += 6;

    // Meta line
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    setC(MUTED);
    const metaParts = [
      sausageStyle,
      cookMethod,
      `Meat: ${meatWeight}${unitSystem === 'Metric (g)' ? 'g' : ' lbs'}`,
      `Total: ${totals?.totalG?.toFixed(0) ?? '—'}g`,
      `Stuffable: ${totals?.stuffable?.toFixed(0) ?? '—'}g`,
      // What comes out of the smoker — the weight you actually package
      `Cooked: ${totals?.cookedG?.toFixed(0) ?? '—'}g (${totals?.cookedLb?.toFixed(2) ?? '—'} lb) at ${totals?.cookLossPct ?? 25}% loss`,
    ].filter(Boolean).join('  ·  ');
    doc.text(metaParts, ML, y);
    y += 3;
    setD([226, 232, 240]);
    doc.line(ML, y, ML + CW, y);
    y += 7;

    // ── 1. INGREDIENTS ──────────────────────────────────────
    sectionHead('Ingredients', BLACK);

    // Column headers
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    setC(MUTED);
    doc.text('Ingredient',     ML,        y);
    doc.text('%',              ML + 100,  y, { align: 'right' });
    doc.text('Grams',          ML + 120,  y, { align: 'right' });
    doc.text('Oz',             ML + 138,  y, { align: 'right' });
    doc.text('Notes',          ML + 143,  y);
    setD([220, 225, 235]);
    doc.line(ML, y + 1.5, ML + CW, y + 1.5);
    y += 5;

    const grouped = {};
    SECTIONS.forEach(s => { grouped[s.key] = []; });
    (filledIngredients || []).forEach(r => {
      if (grouped[r.sectionKey]) grouped[r.sectionKey].push(r);
    });

    SECTIONS.forEach(sect => {
      const rows = grouped[sect.key];
      if (!rows || rows.length === 0) return;

      // Section label — bold black (Fix 5)
      checkY(8);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      setC(BLACK);
      doc.text(sect.label, ML, y);
      y += 5;

      rows.forEach(row => {
        checkY(6);
        const pct   = parseFloat(row.pct) || 0;
        const grams = meatWeightG > 0 ? pctToGrams(pct, meatWeightG).toFixed(1) : '—';
        const oz    = meatWeightG > 0
          ? (pctToGrams(pct, meatWeightG) / 28.3495).toFixed(2) : '—';

        // No alternating row colors (Fix 4)
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        setC(DARK);
        doc.text(row.name || '',     ML + 3,     y);
        doc.text(pct.toFixed(3)+'%', ML + 100,   y, { align: 'right' });
        doc.text(grams + 'g',        ML + 120,   y, { align: 'right' });
        doc.text(oz,                 ML + 138,   y, { align: 'right' });
        if (row.note) {
          doc.setFontSize(7.5);
          setC(MUTED);
          doc.text(row.note.substring(0, 45), ML + 143, y);
        }
        y += 5;
      });
      y += 2;
    });

    gap(3);

    // ── 2. METHODS & INSTRUCTIONS (optional) ────────────────
    if (opts.methods) {
      checkY(15);
      sectionHead('Methods & Instructions', BLACK);

      // Method steps come from the recipe being edited. They used to be read
      // from localStorage with a second copy of the defaults hardcoded here,
      // which drifted from the editor's wording.
      const methodSections = normalizeSections(calc.methodSections);

      methodSections.forEach(sec => {
        subHead(sec.title);
        sec.steps.forEach((step, i) => {
          const label = `${i + 1}.  `;
          const text  = (step.text || step).replace(/\\n/g, '\n');
          checkY(6);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8.5);
          setC(DARK);
          doc.text(label, ML + 3, y);
          doc.setFont('helvetica', 'normal');
          const lines = doc.splitTextToSize(text, CW - 12);
          lines.forEach((l, li) => {
            checkY(5.5);
            doc.text(l, ML + 10, y);
            y += 5;
          });
        });
        gap(3);
      });
    }

    // ── 3. RECIPE NOTES ─────────────────────────────────────
    if (recipeNotes) {
      checkY(15);
      sectionHead('Recipe Notes', BLACK);
      bodyLine(recipeNotes);
      gap(4);
    }

    // ── 4. CASING + NUTRITION side by side (Fixes 6 & 7) ────
    const hasCasingSection = opts.casing && selectedCasing !== null;
    const hasNutrition     = opts.nutrition && nutrition;

    if (hasCasingSection || hasNutrition) {
      checkY(60);
      const halfW  = (CW - 8) / 2;
      const leftX  = ML;
      const rightX = ML + halfW + 8;

      // Draw both sections starting at same y
      const startY = y;

      // ── Left: Casing Calculator ──
      if (hasCasingSection) {
        const casing = CASING_DB[selectedCasing];
        // Shared result — same ft/lb override the sidebar used
        const cr = casingResult;
        const feetNeeded  = cr ? cr.feetNeeded  : 0;
        const feetBuffer  = cr ? cr.feetBuffer  : 0;
        const estLinks    = cr ? cr.estLinks     : 0;
        const effLinkWt   = cr ? cr.effectiveLinkWt : 0;
        const predLinkWt  = cr ? cr.predictedLinkWeightG : 0;
        const cookedLinkG = cr ? cr.cookedLinkG : 0;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        setC(BLUE);
        doc.text('Casing Calculator', leftX, y);
        y += 2;
        doc.setDrawColor(26, 35, 126);
        doc.line(leftX, y, leftX + halfW, y);
        y += 5;
        doc.setFontSize(7.5);

        const casingRows = [
          ['Casing',           casing ? casing[0] : '—'],
          ['Rate Used',        `${cr ? cr.ftPerLb : 0} ft/lb`],
          ['Link Length',      `${linkLength || 6} inches`],
          ['Predicted Link Wt', `${predLinkWt.toFixed(1)} g raw stuffed`],
          ['Avg Sausage Wt',   `${effLinkWt.toFixed(1)} g${linkWeight ? '' : ' (predicted)'}`],
          ['Casing Needed',    `${feetNeeded} ft (${cr ? cr.metersNeeded : 0} m)`],
          ['Buy w/ 15% Buffer', `${feetBuffer} ft`],
          ['Cooked Link Wt',   `${cookedLinkG.toFixed(1)} g after ${cr ? cr.cookLossPct : 25}% loss`],
          ['Est. Sausages',    `${estLinks} sausages at ${linkLength || 6}-inch`],
        ];

        casingRows.forEach(([label, val]) => {
          if (y > PH - 25) return;
          doc.setFont('helvetica', 'bold');
          setC(MUTED);
          doc.text(label + ':', leftX + 2, y);
          doc.setFont('helvetica', 'normal');
          setC(DARK);
          const lines = doc.splitTextToSize(String(val), halfW - 40);
          doc.text(lines[0] || '', leftX + 42, y);
          y += 5;
        });
      }

      // ── Right: Nutrition (same y start) ──
      if (hasNutrition) {
        y = startY;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        setC(GREEN);
        doc.text('Nutrition Estimate', rightX, y);
        y += 2;
        doc.setDrawColor(...GREEN);
        doc.line(rightX, y, rightX + halfW, y);
        y += 3;
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(7);
        setC(MUTED);
        doc.text('per 100g · pre-cook · estimate only', rightX, y);
        y += 5;

        const macros = [
          ['Protein',          nutrition.protein,         'g'],
          ['Carbohydrates',    nutrition.carbs,           'g'],
          ['Fat',              nutrition.fat,             'g'],
          ['Sodium',           nutrition.sodium,          'mg'],
          ['Calories',         nutrition.calories,        'kcal'],
          ['Fat Ratio (total)', nutrition.fatRatioTotal,  '%'],
          ['Fat Ratio (M+F)',   nutrition.fatRatioMFOnly, '%'],
        ];

        doc.setFontSize(7.5);
        macros.forEach(([label, val, unit]) => {
          if (y > PH - 25) return;
          doc.setFont('helvetica', 'bold');
          setC(MUTED);
          doc.text(label + ':', rightX + 2, y);
          doc.setFont('helvetica', 'normal');
          setC(DARK);
          const valStr = typeof val === 'number'
            ? `${val.toFixed(1)} ${unit}/100g`
            : '—';
          doc.text(valStr, rightX + halfW, y, { align: 'right' });
          y += 5;
        });
      }
    }

    // ── FOOTER ───────────────────────────────────────────────
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      setC(MUTED);
      doc.text(
        `LinksLab  ·  ${new Date().toLocaleDateString()}  ·  Page ${i} of ${pageCount}`,
        PW / 2, PH - 8, { align: 'center' }
      );
    }

    const filename = (recipeName || 'recipe')
      .replace(/[^a-z0-9]/gi, '_').toLowerCase() + '.pdf';
    doc.save(filename);
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        <div className={styles.header}>
          <div>
            <div className={styles.title}>Export to PDF</div>
            <div className={styles.subtitle}>{recipeName || 'Untitled Recipe'}</div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Section toggles — Fix 2 */}
        <div className={styles.optionsSection}>
          <div className={styles.optionsTitle}>Include in export:</div>
          <div className={styles.options}>
            <label className={styles.optRow}>
              <span className={styles.optLabel}>
                <span className={styles.optIcon}>📋</span>
                Ingredients
              </span>
              <span className={styles.optAlways}>Always included</span>
            </label>
            {[
              { key: 'methods',   icon: '📝', label: 'Methods & Instructions' },
              { key: 'nutrition', icon: '🥗', label: 'Nutrition Estimate' },
              { key: 'casing',    icon: '🧲', label: 'Casing Calculator',
                disabled: selectedCasing === null,
                hint: selectedCasing === null ? '(no casing selected)' : '' },
            ].map(o => (
              <label key={o.key} className={`${styles.optRow} ${o.disabled ? styles.optDisabled : ''}`}>
                <span className={styles.optLabel}>
                  <span className={styles.optIcon}>{o.icon}</span>
                  {o.label}
                  {o.hint && <span className={styles.optHint}>{o.hint}</span>}
                </span>
                <input
                  type="checkbox"
                  className={styles.optCheck}
                  checked={opts[o.key]}
                  onChange={() => !o.disabled && toggleOpt(o.key)}
                  disabled={o.disabled}
                />
              </label>
            ))}
          </div>
        </div>

        {filledIngredients.length === 0 && (
          <div className={styles.warning}>
            ⚠️ No ingredients entered yet.
          </div>
        )}

        <div className={styles.footer}>
          <button className={styles.btnCancel} onClick={onClose}>Cancel</button>
          <button
            className={`${styles.btnGenerate} ${done ? styles.btnDone : ''}`}
            onClick={handleGenerate}
            disabled={generating || filledIngredients.length === 0}
          >
            {generating ? 'Generating…' : done ? '✓ Downloaded!' : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}
